import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  Building2, Users, Handshake, Home, FolderKanban, CheckCircle2, 
  ChevronDown, ChevronRight, ArrowRight, ExternalLink, TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLIENT_TYPE_CONFIG = {
  investida: { label: "Investidas", icon: Building2, color: "text-purple-500", bg: "bg-purple-500/10" },
  cliente: { label: "Clientes", icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" },
  parceiro: { label: "Parceiros", icon: Handshake, color: "text-green-500", bg: "bg-green-500/10" },
  interno: { label: "Internos", icon: Home, color: "text-orange-500", bg: "bg-orange-500/10" },
};

const STATUS_COLORS = {
  ativo: "bg-green-500",
  inativo: "bg-gray-500",
  em_prospeccao: "bg-yellow-500",
};

export default function CrowdOverviewPage() {
  const { getAuthHeaders, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState({});

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/crowd-overview`, { headers: getAuthHeaders() });
      setData(response.data);
      // Auto-expand clients with projects
      const expanded = {};
      response.data.clients.forEach(c => {
        if (c.project_count > 0) expanded[c.id] = true;
      });
      setExpandedClients(expanded);
    } catch (error) {
      console.error("Fetch overview error:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (token) {
      fetchOverview();
    }
  }, [token, fetchOverview]);

  const toggleClient = (clientId) => {
    setExpandedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  if (loading) {
    return (
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { clients, summary } = data;

  return (
    <div className="p-8 space-y-8" data-testid="crowd-overview-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold heading-font">Visão Geral da Crowd</h1>
          <p className="text-muted-foreground mt-1">Dashboard completo de clientes e projetos</p>
        </div>
        <Link to="/clients">
          <Button variant="outline" data-testid="go-to-clients-btn">
            Gerenciar Clientes
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="col-span-2 md:col-span-1 bg-gradient-to-br from-[#004E8A] to-[#00A7E1] text-white">
          <CardContent className="p-6">
            <p className="text-sm opacity-80">Progresso Geral</p>
            <p className="text-4xl font-bold mt-2">{summary.overall_progress}%</p>
            <Progress value={summary.overall_progress} className="mt-3 bg-white/20" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total Clientes</p>
            <p className="text-3xl font-bold mt-2">{summary.total_clients}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total Projetos</p>
            <p className="text-3xl font-bold mt-2">{summary.total_projects}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total Tarefas</p>
            <p className="text-3xl font-bold mt-2">{summary.total_tasks}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Concluídas</p>
            <p className="text-3xl font-bold mt-2 text-green-600">{summary.total_completed}</p>
          </CardContent>
        </Card>
      </div>

      {/* By Type Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(CLIENT_TYPE_CONFIG).map(([type, config]) => {
          const typeData = summary.by_type[type] || { count: 0, projects: 0, tasks: 0 };
          const Icon = config.icon;
          return (
            <Card key={type} className="hover:shadow-md transition-shadow" data-testid={`type-summary-${type}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.bg}`}>
                    <Icon className={`h-5 w-5 ${config.color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{config.label}</p>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                      <span>{typeData.count} clientes</span>
                      <span>•</span>
                      <span>{typeData.projects} projetos</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Clients with Projects */}
      <Card>
        <CardHeader>
          <CardTitle className="heading-font flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#00A7E1]" />
            Clientes e Projetos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {clients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="text-muted-foreground mt-4">Nenhum cliente cadastrado</p>
              <Link to="/clients">
                <Button variant="outline" className="mt-4">
                  Adicionar Cliente
                </Button>
              </Link>
            </div>
          ) : (
            clients.map((client) => {
              const typeConfig = CLIENT_TYPE_CONFIG[client.client_type] || CLIENT_TYPE_CONFIG.cliente;
              const TypeIcon = typeConfig.icon;
              const isExpanded = expandedClients[client.id];
              
              return (
                <Collapsible key={client.id} open={isExpanded} onOpenChange={() => toggleClient(client.id)}>
                  <div 
                    className="border rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
                    data-testid={`client-row-${client.id}`}
                  >
                    {/* Client Header */}
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors">
                        <div 
                          className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                          style={{ backgroundColor: client.color }}
                        >
                          {client.name[0].toUpperCase()}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{client.name}</span>
                            <Badge variant="secondary" className="text-xs flex items-center gap-1">
                              <TypeIcon className="h-3 w-3" />
                              {typeConfig.label.slice(0, -1)}
                            </Badge>
                            <div className={`h-2 w-2 rounded-full ${STATUS_COLORS[client.status] || STATUS_COLORS.ativo}`} />
                            {client.category && (
                              <Badge variant="outline" className="text-xs">{client.category}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <FolderKanban className="h-3 w-3" />
                              {client.project_count} projetos
                            </span>
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              {client.completed_count}/{client.task_count} tarefas
                            </span>
                          </div>
                        </div>
                        {client.project_count > 0 && (
                          <div className="flex items-center gap-3">
                            <div className="w-24">
                              <Progress 
                                value={client.task_count > 0 ? (client.completed_count / client.task_count * 100) : 0} 
                                className="h-2"
                              />
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                        )}
                      </div>
                    </CollapsibleTrigger>

                    {/* Projects List */}
                    <CollapsibleContent>
                      {client.projects.length > 0 && (
                        <div className="border-t bg-muted/30">
                          {client.projects.map((project, idx) => (
                            <Link 
                              key={project.id} 
                              to={`/projects/${project.id}`}
                              className={`flex items-center gap-4 p-4 pl-16 hover:bg-muted/50 transition-colors ${idx !== client.projects.length - 1 ? 'border-b' : ''}`}
                              data-testid={`project-row-${project.id}`}
                            >
                              <div 
                                className="h-10 w-10 rounded-lg bg-cover bg-center shrink-0"
                                style={{ backgroundImage: `url(${project.cover_image})` }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{project.name}</p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {project.description || "Sem descrição"}
                                </p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="text-sm font-medium">{project.progress}%</p>
                                  <p className="text-xs text-muted-foreground">
                                    {project.completed_count}/{project.task_count} tarefas
                                  </p>
                                </div>
                                <div className="w-20">
                                  <Progress value={project.progress} className="h-2" />
                                </div>
                                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
